import "./style.css";
import { PRESENTATION_IR_VERSION, type PresentationEvent, type PresentationFrame } from "./core";
import { TRACES } from "./demo/traces";
import { LabRenderer } from "./renderer/lab-renderer";
import { PALETTES, type RenderStyle } from "./renderer/styles";
import {
  CharacterStudioSource,
  CourtLiveSource,
  MotionYardSource,
  TraceSource,
  type PresentationSource,
  type YardAbilitySlot,
} from "./sources";
import { HudLayer } from "./ui";

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) throw new Error("Missing #app mount.");

app.innerHTML = `
  <main class="lab-shell">
    <header class="masthead">
      <div>
        <p class="eyebrow">Presentation Engine · First Fire</p>
        <h1>One semantic performance.<br><span>Any visual language.</span></h1>
      </div>
      <div class="engine-status">
        <span class="pulse"></span>
        <div><strong>Runtime online</strong><small>${PRESENTATION_IR_VERSION}</small></div>
      </div>
    </header>

    <section class="workbench">
      <div class="stage-column">
        <div class="stage-frame">
          <div id="stage"></div>
          <div id="yard-overlay" class="yard-overlay" hidden>
            <div class="yard-help"><strong>WASD</strong> move <span>·</span> <strong>drag</strong> orbit <span>·</span> <strong>wheel</strong> zoom</div>
            <div class="yard-abilities" aria-label="Motion Yard abilities">
              <button data-yard-ability="1" aria-label="Ability 1: Arc Sweep"><kbd>1</kbd><span><strong>Arc Sweep</strong><small>wide cut</small></span></button>
              <button data-yard-ability="2" aria-label="Ability 2: Needle Rush"><kbd>2</kbd><span><strong>Needle Rush</strong><small>line thrust</small></span></button>
              <button data-yard-ability="3" aria-label="Ability 3: Windstep"><kbd>3</kbd><span><strong>Windstep</strong><small>dash slash</small></span></button>
              <button data-yard-ability="4" aria-label="Ability 4: Sunbreak"><kbd>4</kbd><span><strong>Sunbreak</strong><small>radial hit</small></span></button>
            </div>
          </div>
          <div class="stage-topline">
            <span id="trace-source"></span>
            <span id="clock">00.00 / 00.00</span>
          </div>
          <div class="stage-caption">
            <strong id="phase-label">READY</strong>
            <span id="phase-detail">Semantic trace standing by</span>
          </div>
        </div>
        <div class="transport">
          <button id="restart" class="icon-button" aria-label="Restart trace">↺</button>
          <button id="play" class="play-button" aria-label="Play or pause">Pause</button>
          <input id="scrubber" aria-label="Trace time" type="range" min="0" max="11.5" value="0" step="0.01" />
        </div>
      </div>

      <aside class="control-deck">
        <div class="deck-heading"><span>Compiler inputs</span><small>LIVE</small></div>
        <label>State source<select id="source-select"></select></label>
        <div class="yard-control yard-run-control" hidden>
          <button id="yard-replay" type="button">Replay recorded run</button>
          <small id="yard-run-state">INPUT RECORDING</small>
        </div>
        <label class="studio-control" hidden>Motion preview<select id="studio-motion">
          <option value="idle">Idle</option>
          <option value="locomotion">Locomotion</option>
          <option value="action">Action cycle</option>
          <option value="hit">Hit reaction</option>
          <option value="defeated">Defeated</option>
        </select></label>
        <label class="studio-control" hidden>Action policy<select id="studio-action-policy">
          <option value="cleave">Overhead cleave</option>
          <option value="sweep">Lateral sweep</option>
          <option value="thrust">Advancing thrust</option>
          <option value="shieldBash">Shield bash</option>
          <option value="risingSlash">Rising diagonal</option>
        </select></label>
        <label class="studio-control" hidden>Chain entry<select id="studio-chain-from">
          <option value="none">Neutral / idle</option>
          <option value="sweep">After lateral sweep</option>
          <option value="cleave">After overhead cleave</option>
          <option value="risingSlash">After rising diagonal</option>
          <option value="thrust">After advancing thrust</option>
          <option value="shieldBash">After shield bash</option>
        </select></label>
        <div class="studio-control studio-phase-controls" hidden>
          <span>Pose checkpoints</span>
          <div>
            <button type="button" data-studio-phase="telegraph">Telegraph</button>
            <button type="button" data-studio-phase="commit">Commit</button>
            <button type="button" data-studio-phase="impact">Contact</button>
            <button type="button" data-studio-phase="recover">Follow</button>
          </div>
        </div>
        <label class="studio-control" hidden>Playback speed<select id="studio-speed">
          <option value="0.25">0.25×</option>
          <option value="0.5">0.5×</option>
          <option value="1" selected>1×</option>
          <option value="2">2×</option>
        </select></label>
        <label class="range-label studio-control" hidden><span>Action energy <output id="energy-output">88%</output></span><input id="action-energy" aria-label="Action energy" type="range" min="0" max="100" value="88" step="1" /></label>
        <label class="range-label studio-control" hidden><span>Action reach <output id="reach-output">76%</output></span><input id="action-reach" aria-label="Action reach" type="range" min="0" max="100" value="76" step="1" /></label>
        <label class="range-label studio-control" hidden><span>Forward drive <output id="advance-output">55%</output></span><input id="action-advance" aria-label="Action forward drive" type="range" min="0" max="100" value="55" step="1" /></label>
        <label class="studio-control" hidden>Equipment recipe<select id="studio-loadout">
          <option value="full">Warrior · full kit</option>
          <option value="light">Vanguard · light kit</option>
          <option value="sentinel">Sentinel · shield kit</option>
          <option value="base">Unarmored baseline</option>
        </select></label>
        <fieldset class="studio-control color-control" hidden>
          <legend>Color slots</legend>
          <label>Body<input id="body-color" aria-label="Body color" type="color" value="#3fa7d6" /></label>
          <label>Accent<input id="accent-color" aria-label="Accent color" type="color" value="#b8f3ff" /></label>
          <label>Dark<input id="dark-color" aria-label="Dark color" type="color" value="#16141a" /></label>
        </fieldset>
        <label class="range-label studio-control" hidden><span>Character rotation <output id="character-output">0°</output></span><input id="character-rotation" aria-label="Character rotation" type="range" min="0" max="360" value="0" step="1" /></label>
        <label>Style pack<select id="palette-select"></select></label>
        <label class="range-label"><span>Pixel scale <output id="pixel-output">4×</output></span><input id="pixel-scale" aria-label="Pixel scale" type="range" min="1" max="8" value="4" step="1" /></label>
        <label class="range-label"><span>Lighting bands <output id="bands-output">4</output></span><input id="bands" aria-label="Lighting bands" type="range" min="2" max="8" value="4" step="1" /></label>
        <label class="range-label"><span>Motion emphasis <output id="motion-output">100%</output></span><input id="motion" aria-label="Motion emphasis" type="range" min="60" max="180" value="100" step="5" /></label>
        <label class="range-label"><span>World wind <output id="wind-output">75%</output></span><input id="wind" aria-label="World wind" type="range" min="0" max="160" value="75" step="5" /></label>
        <label class="range-label"><span>Camera pitch <output id="camera-output">52°</output></span><input id="camera" aria-label="Camera pitch" type="range" min="32" max="70" value="52" step="1" /></label>
        <label class="range-label"><span>Camera orbit <output id="orbit-output">42°</output></span><input id="camera-orbit" aria-label="Camera orbit" type="range" min="0" max="360" value="42" step="1" /></label>
        <label class="range-label"><span>Camera zoom <output id="zoom-output">5.3</output></span><input id="camera-zoom" aria-label="Camera zoom" type="range" min="1.8" max="7" value="5.3" step="0.1" /></label>

        <div class="contract-card">
          <p>Renderer contract</p>
          <div><span>Events</span><strong>spawn · move · act · impact · status</strong></div>
          <div><span>Game logic</span><strong id="authority-label">Court simulation</strong></div>
          <div><span>World backend</span><strong>Three.js · WebGL</strong></div>
          <div><span>HUD backend</span><strong>DOM · full resolution</strong></div>
          <div><span>Golden kit</span><strong>Court Warrior · v1</strong></div>
        </div>
      </aside>
    </section>

    <section class="telemetry">
      <div class="telemetry-heading"><span>Semantic event stream</span><small id="event-count"></small></div>
      <div id="event-stream" class="event-stream"></div>
    </section>
  </main>
`;

function required<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) throw new Error(`Missing ${selector}`);
  return element;
}

const sourceSelect = required<HTMLSelectElement>("#source-select");
const paletteSelect = required<HTMLSelectElement>("#palette-select");
const characterStudio = new CharacterStudioSource();
const motionYard = new MotionYardSource();
const sources: PresentationSource[] = [
  motionYard,
  new CourtLiveSource(),
  characterStudio,
  ...TRACES.map((trace) => new TraceSource(trace)),
];
for (const source of sources) sourceSelect.add(new Option(source.title, source.id));
for (const palette of PALETTES) paletteSelect.add(new Option(palette.name, palette.id));

const stage = required<HTMLDivElement>("#stage");
const lab = new LabRenderer(stage);
const hud = new HudLayer(stage, lab);
const playButton = required<HTMLButtonElement>("#play");
const restartButton = required<HTMLButtonElement>("#restart");
const scrubber = required<HTMLInputElement>("#scrubber");
const traceSource = required<HTMLSpanElement>("#trace-source");
const clock = required<HTMLSpanElement>("#clock");
const phaseLabel = required<HTMLElement>("#phase-label");
const phaseDetail = required<HTMLElement>("#phase-detail");
const eventStream = required<HTMLDivElement>("#event-stream");
const eventCount = required<HTMLElement>("#event-count");
const yardOverlay = required<HTMLElement>("#yard-overlay");
const yardReplay = required<HTMLButtonElement>("#yard-replay");
const yardRunState = required<HTMLElement>("#yard-run-state");
const pressedKeys = new Set<string>();
const studioSpeed = required<HTMLSelectElement>("#studio-speed");

let source = sources[0]!;
let playing = true;
let lastFrame = performance.now();

function numberValue(selector: string): number {
  return Number(required<HTMLInputElement>(selector).value);
}

function currentStyle(): RenderStyle {
  const palette = PALETTES.find((item) => item.id === paletteSelect.value) ?? PALETTES[0]!;
  const cameraTarget = source.cameraTarget ?? { x: 0, y: source.mode === "studio" ? 1.25 : 0.42, z: 0 };
  return {
    palette,
    pixelScale: numberValue("#pixel-scale"),
    lightingBands: numberValue("#bands"),
    motionExaggeration: numberValue("#motion") / 100,
    windStrength: numberValue("#wind") / 100,
    cameraPitch: numberValue("#camera"),
    cameraYaw: numberValue("#camera-orbit"),
    cameraZoom: numberValue("#camera-zoom"),
    cameraTarget,
  };
}

function describeEvent(event: PresentationEvent): { actor: string; verb: string; detail: string } {
  if (event.type === "spawn") return { actor: event.entityId, verb: "SPAWN", detail: "enters presentation" };
  if (event.type === "move") return { actor: event.entityId, verb: event.gait?.toUpperCase() ?? "MOVE", detail: `${event.duration.toFixed(2)}s trajectory` };
  if (event.type === "act") return { actor: event.entityId, verb: "ACT", detail: `${event.action}${event.targetId ? ` → ${event.targetId}` : ""}` };
  if (event.type === "impact") return { actor: event.sourceId, verb: event.outcome.toUpperCase(), detail: `${event.targetId}${event.amount ? ` · ${event.amount}` : ""}` };
  if (event.type === "status") return { actor: event.entityId, verb: "STATUS", detail: `${event.status} ${event.active ? "applied" : "cleared"}` };
  return { actor: event.entityId, verb: "DESPAWN", detail: "leaves presentation" };
}

function renderTelemetry(frame: PresentationFrame): void {
  const visibleEntities = frame.entities.filter((entity) => entity.visible).length;
  eventCount.textContent = `${source.events.length} EVENTS · ${visibleEntities} LIVE ENTITIES`;
  const context = source.events.filter((event) => event.at <= source.time).slice(-6).reverse();
  eventStream.innerHTML = context.map((event) => {
    const description = describeEvent(event);
    const active = frame.recentEvents.some((item) => item.id === event.id) ? " active" : "";
    return `<article class="event-row${active}"><time>${event.at.toFixed(2)}</time><strong>${description.verb}</strong><span>${description.actor}</span><p>${description.detail}</p></article>`;
  }).join("") || `<p class="empty-events">Waiting for the first semantic event…</p>`;
}

function syncControlLabels(): void {
  required<HTMLOutputElement>("#pixel-output").value = `${numberValue("#pixel-scale")}×`;
  required<HTMLOutputElement>("#bands-output").value = String(numberValue("#bands"));
  required<HTMLOutputElement>("#motion-output").value = `${numberValue("#motion")}%`;
  required<HTMLOutputElement>("#wind-output").value = `${numberValue("#wind")}%`;
  required<HTMLOutputElement>("#camera-output").value = `${numberValue("#camera")}°`;
  required<HTMLOutputElement>("#orbit-output").value = `${numberValue("#camera-orbit")}°`;
  required<HTMLOutputElement>("#zoom-output").value = numberValue("#camera-zoom").toFixed(1);
  required<HTMLOutputElement>("#character-output").value = `${numberValue("#character-rotation")}°`;
  required<HTMLOutputElement>("#energy-output").value = `${numberValue("#action-energy")}%`;
  required<HTMLOutputElement>("#reach-output").value = `${numberValue("#action-reach")}%`;
  required<HTMLOutputElement>("#advance-output").value = `${numberValue("#action-advance")}%`;
}

function syncSource(): void {
  source = sources.find((item) => item.id === sourceSelect.value) ?? sources[0]!;
  source.reset();
  scrubber.max = String(source.duration);
  scrubber.value = "0";
  scrubber.disabled = !source.canSeek;
  scrubber.title = source.canSeek ? "Trace time" : "Live sources replay or restart from their own authoritative state.";
  traceSource.textContent = source.sourceLabel;
  required<HTMLElement>(".deck-heading small").textContent = source.mode.toUpperCase();
  required<HTMLElement>("#authority-label").textContent = source.mode === "live"
    ? "Court simulation"
    : source.mode === "game"
      ? "Motion Yard simulation"
    : source.mode === "studio"
      ? "renderer inspection"
      : "recorded fixture";
  const studioActive = source === characterStudio;
  const yardActive = source === motionYard;
  for (const control of document.querySelectorAll<HTMLElement>(".studio-control")) control.hidden = !studioActive;
  for (const control of document.querySelectorAll<HTMLElement>(".yard-control")) control.hidden = !yardActive;
  yardOverlay.hidden = !yardActive;
  required<HTMLElement>(".stage-frame").classList.toggle("is-interactive", yardActive);
  pressedKeys.clear();
  const zoom = required<HTMLInputElement>("#camera-zoom");
  zoom.value = studioActive ? "2.3" : yardActive ? "3.75" : "5.3";
  required<HTMLInputElement>("#camera").value = studioActive ? "36" : yardActive ? "48" : "52";
  if (yardActive) required<HTMLInputElement>("#camera-orbit").value = String(motionYard.cameraYaw);
  syncControlLabels();
  syncYardControls();
}

sourceSelect.addEventListener("change", syncSource);
playButton.addEventListener("click", () => {
  playing = !playing;
  playButton.textContent = playing ? "Pause" : "Play";
});
restartButton.addEventListener("click", () => {
  source.reset();
  playing = true;
  playButton.textContent = "Pause";
});
scrubber.addEventListener("input", () => {
  source.seek(Number(scrubber.value));
  playing = false;
  playButton.textContent = "Play";
});
required<HTMLSelectElement>("#studio-motion").addEventListener("change", (event) => {
  characterStudio.setMotion((event.currentTarget as HTMLSelectElement).value as "idle" | "locomotion" | "action" | "hit" | "defeated");
});
required<HTMLSelectElement>("#studio-action-policy").addEventListener("change", (event) => {
  characterStudio.setActionPolicy((event.currentTarget as HTMLSelectElement).value as "cleave" | "sweep" | "thrust" | "shieldBash" | "risingSlash");
  const intent = characterStudio.actionIntent;
  required<HTMLInputElement>("#action-energy").value = String(Math.round(intent.energy * 100));
  required<HTMLInputElement>("#action-reach").value = String(Math.round(intent.reach * 100));
  required<HTMLInputElement>("#action-advance").value = String(Math.round(intent.advance * 100));
  syncControlLabels();
});
required<HTMLSelectElement>("#studio-chain-from").addEventListener("change", (event) => {
  const value = (event.currentTarget as HTMLSelectElement).value;
  characterStudio.setTransitionFrom(value === "none" ? null : value as "cleave" | "sweep" | "thrust" | "shieldBash" | "risingSlash");
  required<HTMLSelectElement>("#studio-motion").value = "action";
  playing = true;
  playButton.textContent = "Pause";
});
for (const button of document.querySelectorAll<HTMLButtonElement>("[data-studio-phase]")) {
  button.addEventListener("click", () => {
    if (source !== characterStudio) return;
    characterStudio.seekActionPhase(button.dataset.studioPhase as "telegraph" | "commit" | "impact" | "recover");
    required<HTMLSelectElement>("#studio-motion").value = "action";
    playing = false;
    playButton.textContent = "Play";
  });
}
for (const [selector, parameter] of [["#action-energy", "energy"], ["#action-reach", "reach"], ["#action-advance", "advance"]] as const) {
  required<HTMLInputElement>(selector).addEventListener("input", (event) => {
    characterStudio.setActionParameter(parameter, Number((event.currentTarget as HTMLInputElement).value) / 100);
  });
}
required<HTMLSelectElement>("#studio-loadout").addEventListener("change", (event) => {
  characterStudio.setLoadout((event.currentTarget as HTMLSelectElement).value as "full" | "light" | "sentinel" | "base");
});
for (const slot of ["body", "accent", "dark"] as const) {
  required<HTMLInputElement>(`#${slot}-color`).addEventListener("input", (event) => {
    characterStudio.setColor(slot, (event.currentTarget as HTMLInputElement).value);
  });
}
required<HTMLInputElement>("#character-rotation").addEventListener("input", (event) => {
  characterStudio.setRotation(Number((event.currentTarget as HTMLInputElement).value));
});
yardReplay.addEventListener("click", () => {
  if (source === motionYard && motionYard.replayRun()) {
    pressedKeys.clear();
    playing = true;
    playButton.textContent = "Pause";
  }
});
for (const button of document.querySelectorAll<HTMLButtonElement>("[data-yard-ability]")) {
  button.addEventListener("click", () => {
    if (source === motionYard) motionYard.triggerAbility(Number(button.dataset.yardAbility) as YardAbilitySlot);
  });
}

function isTypingTarget(target: EventTarget | null): boolean {
  return target instanceof HTMLInputElement || target instanceof HTMLSelectElement || target instanceof HTMLTextAreaElement;
}

window.addEventListener("keydown", (event) => {
  if (source !== motionYard || isTypingTarget(event.target)) return;
  if (["KeyW", "KeyA", "KeyS", "KeyD"].includes(event.code)) {
    pressedKeys.add(event.code);
    event.preventDefault();
  }
  if (!event.repeat && /^Digit[1-4]$/.test(event.code)) {
    motionYard.triggerAbility(Number(event.code.at(-1)) as YardAbilitySlot);
    event.preventDefault();
  }
});
window.addEventListener("keyup", (event) => {
  pressedKeys.delete(event.code);
});
window.addEventListener("blur", () => pressedKeys.clear());

const stageFrame = required<HTMLElement>(".stage-frame");
let orbitPointer: { id: number; x: number; y: number } | null = null;
stageFrame.addEventListener("pointerdown", (event) => {
  if (source !== motionYard || (event.target as Element).closest("button")) return;
  orbitPointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
  stageFrame.setPointerCapture(event.pointerId);
  event.preventDefault();
});
stageFrame.addEventListener("pointermove", (event) => {
  if (!orbitPointer || orbitPointer.id !== event.pointerId || source !== motionYard) return;
  const orbit = required<HTMLInputElement>("#camera-orbit");
  const pitch = required<HTMLInputElement>("#camera");
  const deltaX = event.clientX - orbitPointer.x;
  const deltaY = event.clientY - orbitPointer.y;
  orbitPointer = { id: event.pointerId, x: event.clientX, y: event.clientY };
  orbit.value = String((Number(orbit.value) - deltaX * 0.34 + 360) % 360);
  pitch.value = String(Math.max(32, Math.min(70, Number(pitch.value) - deltaY * 0.22)));
  motionYard.setCameraYaw(Number(orbit.value));
  syncControlLabels();
});
stageFrame.addEventListener("pointerup", (event) => {
  orbitPointer = null;
  if (stageFrame.hasPointerCapture(event.pointerId)) stageFrame.releasePointerCapture(event.pointerId);
});
stageFrame.addEventListener("wheel", (event) => {
  if (source !== motionYard) return;
  const zoom = required<HTMLInputElement>("#camera-zoom");
  zoom.value = String(Math.max(1.8, Math.min(7, Number(zoom.value) + Math.sign(event.deltaY) * 0.18)));
  syncControlLabels();
  event.preventDefault();
}, { passive: false });
for (const input of document.querySelectorAll<HTMLInputElement>("input[type=range]")) input.addEventListener("input", syncControlLabels);

syncSource();
syncControlLabels();

function tick(now: number): void {
  const delta = Math.min(0.05, (now - lastFrame) / 1000);
  lastFrame = now;
  if (source === motionYard) {
    motionYard.setMovementInput(
      (pressedKeys.has("KeyW") ? 1 : 0) - (pressedKeys.has("KeyS") ? 1 : 0),
      (pressedKeys.has("KeyD") ? 1 : 0) - (pressedKeys.has("KeyA") ? 1 : 0),
    );
    if (motionYard.isReplaying) {
      required<HTMLInputElement>("#camera-orbit").value = String(motionYard.cameraYaw);
    } else {
      motionYard.setCameraYaw(numberValue("#camera-orbit"));
    }
  }
  if (playing) source.step(source === characterStudio ? delta * Number(studioSpeed.value) : delta);

  const frame = source.sample();
  lab.render(frame, source.id, currentStyle());
  hud.render(frame);
  scrubber.value = String(source.time);
  traceSource.textContent = source.sourceLabel;
  clock.textContent = `${source.time.toFixed(2).padStart(5, "0")} / ${source.duration.toFixed(2)}`;
  const acting = frame.entities.find((entity) => entity.action)?.action;
  const latest = frame.recentEvents.at(-1);
  phaseLabel.textContent = acting?.phase.toUpperCase() ?? (latest?.type.toUpperCase() ?? "READY");
  phaseDetail.textContent = acting ? `${acting.action} · ${(acting.phaseProgress * 100).toFixed(0)}%` : (latest ? describeEvent(latest).detail : "Semantic trace standing by");
  renderTelemetry(frame);
  syncYardControls();
  requestAnimationFrame(tick);
}

function syncYardControls(): void {
  if (source !== motionYard) return;
  yardReplay.disabled = !motionYard.canReplay;
  yardRunState.textContent = motionYard.isReplaying ? "REPLAYING INPUT" : "INPUT RECORDING";
  for (const ability of motionYard.abilityStates) {
    const button = required<HTMLButtonElement>(`[data-yard-ability="${ability.slot}"]`);
    button.disabled = ability.remaining > 0 || motionYard.isReplaying;
    const status = button.querySelector("small");
    if (status) status.textContent = ability.remaining > 0 ? `${ability.remaining.toFixed(1)}s` : ability.summary;
  }
}

requestAnimationFrame(tick);
